import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import { formatIndex, formatNumber } from '@sohanscript/lib/utils/format';
import { FilterPills } from '@sohanscript/ui/filter-pills';
import { List, ListItem } from '@sohanscript/ui/list';
import { Motion } from '@sohanscript/ui/motion';
import {
	type ComponentPropsWithoutRef,
	useState,
	useSyncExternalStore,
} from 'react';
import { Link } from '@/components/link';
import { Button } from '@/components/ui/button';
import type { Service, ServiceCategory } from '@/lib/data/services';
import { categoryLabels } from '@/lib/data/services-i18n';
import { CheckTags } from './check-tags';
import { StackPills } from './stack-pills';
import { TelemetryRows } from './telemetry-rows';

// NOTE: Mobile collapses the ledger after this many rows, desktop shows all.
const MOBILE_VISIBLE_COUNT = 9;

const hashFor = (slug: string) => `svc-${slug}`;

function slugFromHash(): string | null {
	if (typeof window === 'undefined') return null;
	const hash = window.location.hash.replace(/^#/, '');
	return hash.startsWith('svc-') ? hash.slice(4) : null;
}

const SERVICE_HASH_CHANGE = 'service-ledger:hashchange'; // @icon-ignore

function subscribeToHashChange(onStoreChange: () => void) {
	window.addEventListener('hashchange', onStoreChange);
	window.addEventListener(SERVICE_HASH_CHANGE, onStoreChange);
	return () => {
		window.removeEventListener('hashchange', onStoreChange);
		window.removeEventListener(SERVICE_HASH_CHANGE, onStoreChange);
	};
}

function getServerExpandedSnapshot() {
	return null;
}

function replaceHash(hash: string) {
	window.history.replaceState(null, '', hash);
	window.dispatchEvent(new Event(SERVICE_HASH_CHANGE));
}

type LedgerRowProps = ComponentPropsWithoutRef<'article'> & {
	service: Service;
	numeral: string;
	open: boolean;
	onExpand: (slug: string) => void;
};

export function LedgerRow({
	service,
	numeral,
	open,
	onExpand,
	className,
	...props
}: LedgerRowProps) {
	return (
		<ListItem as="article" className={className} {...props}>
			<Link
				to="/build/$slug"
				viewTransition
				state={{ transitionName: 'vt-service' }}
				params={{ slug: service.slug } as never}
				aria-label={service.title}
				className="absolute inset-0 z-10 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
			/>

			<div className="flex items-center gap-3 px-1 py-4 md:gap-4 md:py-5">
				<span className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/5 text-primary transition-colors duration-300 group-hover:border-primary/50 group-hover:bg-primary/10">
					<Iconify icon={service.icon} className="size-5" aria-hidden="true" />
				</span>
				<span className="hidden w-16 shrink-0 flex-col leading-none sm:flex">
					<span className="font-mono text-muted-foreground text-xs tabular-nums transition-colors duration-300 group-hover:text-primary group-focus-within:text-primary">
						{numeral}
					</span>
					<span className="mt-1.5 truncate font-mono text-2xs text-muted-foreground uppercase tracking-widest">
						{service.categoryLabel}
					</span>
				</span>
				<span className="min-w-0 flex-1">
					<span className="block truncate font-medium text-base text-foreground transition-colors duration-300 group-hover:text-primary group-focus-within:text-primary md:text-lg">
						{service.title}
					</span>
					<span
						className={cn(
							'mt-0.5 block text-foreground/75 text-sm leading-relaxed',
							!open && 'truncate ',
						)}
					>
						{service.tagline}
					</span>
				</span>
				{!open && (
					<StackPills
						items={service.stack}
						limit={4}
						className="hidden shrink-0 items-center lg:flex"
					/>
				)}
				<Button
					variant="ghost"
					size="icon"
					type="button"
					onClick={() => onExpand(service.slug)}
					aria-expanded={open}
					aria-label={service.title}
					className="relative z-20 size-9 shrink-0 rounded-lg border border-primary/20 text-primary/70 hover:border-primary/50 hover:text-primary"
				>
					<Iconify
						icon="lucide:chevron-down"
						className={cn(
							'size-4 transition-transform duration-300',
							open && 'rotate-180',
						)}
						aria-hidden="true"
					/>
				</Button>
			</div>

			<div
				className={cn(
					'grid transition-all duration-300 ease-out',
					open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
				)}
			>
				<div className="overflow-hidden" inert={!open}>
					<div className="relative z-20 grid gap-6 lg:gap-20 pe-1 ps-14 pt-1 pb-6 md:grid-cols-2 md:ps-35">
						<ul className="divide-y divide-border/40 self-start border-border/40 border-y">
							{service.scope.map((item) => (
								<li key={item.id} className="flex flex-col gap-1 py-2.5">
									<span className="font-medium text-primary text-sm">
										{item.title}
									</span>
									<CheckTags tags={item.tags} className="gap-y-1 font-mono" />
								</li>
							))}
						</ul>
						<div className="flex flex-col gap-4">
							<StackPills items={service.stack} />
							<TelemetryRows params={service.telemetry} />
							<Link
								to="/build/$slug"
								viewTransition
								state={{ transitionName: 'vt-service' }}
								params={{ slug: service.slug } as never}
								className="flex w-fit items-center gap-1 font-medium text-primary text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
							>
								{m.svc_card_explore()}
								<Iconify
									icon="lucide:arrow-right"
									className="size-4 rtl:-scale-x-100"
									aria-hidden="true"
								/>
							</Link>
						</div>
					</div>
				</div>
			</div>
		</ListItem>
	);
}

type ServiceLedgerProps = ComponentPropsWithoutRef<'section'> & {
	services: Service[];
	filter: ServiceCategory | null;
	onFilterChange: (category: ServiceCategory | null) => void;
};

export function ServiceLedger({
	services,
	filter,
	onFilterChange,
	className,
	...props
}: ServiceLedgerProps) {
	const expanded = useSyncExternalStore(
		subscribeToHashChange,
		slugFromHash,
		getServerExpandedSnapshot,
	);
	const [showAll, setShowAll] = useState(false);

	const toggleRow = (slug: string) => {
		if (expanded === slug) {
			if (slugFromHash() === slug) {
				replaceHash(window.location.pathname + window.location.search);
			}
		} else {
			replaceHash(`#${hashFor(slug)}`);
		}
	};

	const visible = filter
		? services.filter((s) => s.category === filter)
		: services;
	const order = new Map(visible.map((s, i) => [s.slug, i + 1]));
	const remaining = visible.length - MOBILE_VISIBLE_COUNT;

	const groups: { category: ServiceCategory; items: Service[] }[] = [];
	for (const service of services) {
		const group = groups.find((g) => g.category === service.category);
		if (group) group.items.push(service);
		else groups.push({ category: service.category, items: [service] });
	}

	return (
		<section className={cn(className)} {...props}>
			<FilterPills
				className="pb-4 md:pb-8"
				value={filter}
				onChange={onFilterChange}
				options={[
					{ value: null, label: m.svc_all() },
					...groups.map((g) => ({
						value: g.category,
						label: categoryLabels[g.category](),
					})),
				]}
			/>

			<div>
				{groups.map((group, groupIndex) => {
					const items = group.items.filter(
						(s) => !filter || s.category === filter,
					);
					if (items.length === 0) return null;
					const slug = group.category.toLowerCase().replace(/[^a-z0-9]+/g, '-');
					return (
						<Motion
							key={group.category}
							initial={{ opacity: 0, y: 16 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, margin: '-60px' }}
							transition={{ duration: 0.4, delay: groupIndex * 0.05 }}
						>
							<h2
								className={cn(
									'font-mono pb-2 pt-8 text-2xs uppercase tracking-widest',
									groupIndex === 0 && 'pt-0',
								)}
							>
								<span className="text-primary">~/{slug}</span>
							</h2>
							<List>
								{items.map((service, itemIndex) => (
									<LedgerRow
										key={service.slug}
										service={service}
										numeral={formatIndex(itemIndex + 1)}
										open={expanded === service.slug}
										onExpand={toggleRow}
										className={cn(
											!showAll &&
												(order.get(service.slug) ?? 1) > MOBILE_VISIBLE_COUNT &&
												'max-md:hidden',
										)}
									/>
								))}
							</List>
						</Motion>
					);
				})}
			</div>

			{remaining > 0 && (
				<div className="mt-6 flex justify-center md:hidden">
					<button
						type="button"
						onClick={() => setShowAll((v) => !v)}
						aria-expanded={showAll}
						className="rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 font-mono text-2xs text-primary uppercase tracking-widest transition-colors hover:bg-primary/10"
					>
						{showAll
							? m.svc_show_less()
							: m.svc_show_all({ count: formatNumber(remaining) })}
					</button>
				</div>
			)}
		</section>
	);
}
